import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";

import { API, graphqlOperation } from "aws-amplify";
import { timeslotByYearByWeek } from "../../graphql/event/event-queries";

import {
  Avatar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Popper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import TodayOutlinedIcon from "@material-ui/icons/TodayOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import StarIcon from "@material-ui/icons/Star";
import { blue, blueGrey, grey } from "@material-ui/core/colors";

import {
  add,
  differenceInMinutes,
  format,
  set,
  startOfDay,
  startOfHour,
  startOfWeek,
} from "date-fns";

import { useNotify } from "react-admin";

import { logError } from "../../utils/appUtils";
import {
  applyWallClockFromDate,
  compareWorkdayTimeStrings,
  formatYardScheduleDate,
  getYardCalendarDateParts,
  getYardCalendarDayOfWeek,
  getYardNow,
  isSameYardScheduleDay,
  setDayFromWorkdayTimeString,
  validateAppointment,
} from "../../utils/dateUtils";
import {
  accountColorsByActiveByName,
  productColorsByReloadYard,
} from "../../graphql/account/account-queries";
import {
  CalendarLayout,
  EventType,
  InboundStatuses,
  OutboundStatusLabels,
  usHolidays,
} from "../../utils/constants";
import {
  getShipmentBackedProductName,
  resolveShipmentBackedAccountColor,
  resolveShipmentBackedProductColor,
  setAccountColorMap,
  setProductColorMap,
} from "../../utils/trackListUtils";
import {
  getScheduleDirectionColor,
  getScheduleStatusBadgeStyle,
  getShipsTogetherBadgeStyle,
  getTrackManagementTimeslotEventBodyStyle,
} from "../../utils/trackManagementPalette";
import {
  fetchShipWithMembersCached,
  isLinked,
  shipWithCacheKey,
} from "../../utils/shipWith";
import {
  calendarDayKey,
  coerceFormArrayField,
  resolveHolidayRulesToBlockedDates,
} from "../../utils/holidayRules";
import { deepRedirect } from "../../utils/utils";
import {
  buildDayViewLaneLayout,
  getEventDisplayHeight,
  getResponsiveDayViewClusterThreshold,
  getSingleEventRenderHeight,
  getTooltipAnchorStyle,
  groupOverlappingEvents,
  isEmptySlotDayDisabled,
  MIN_EVENT_DISPLAY_HEIGHT,
} from "./EventsCalendarUtils";
import {
  getDayTimeslots,
  initializeSchedulingState,
  shouldDisableDate,
} from "./scheduler/SchedulerUtils";
import {
  CancelIcon,
  InboundIcon,
  LinkIcon,
  OutboundIcon,
} from "./iconography";
import ShipsTogetherBadge from "./shipWith/ShipsTogetherBadge";

const REFRESH_INTERVAL = 20 * 1000; // 20 seconds
const borderColorDark = blueGrey[700];
const borderColorLight = blueGrey[100];

// Shared constants that can be accessed by all components
const SLOT_HEIGHT = 48;
const COLUMN_WIDTH = 95; // right side column width percentage (for non-small screens)
const DEFAULT_BUSINESS_START_TIME = "6:00am";
const DEFAULT_BUSINESS_END_TIME = "9:00pm";

// Helper function to calculate business hours from workdays
const calculateBusinessHours = (workdays) => {
  if (!workdays?.length) {
    return {
      businessStart: setDayFromWorkdayTimeString(
        getYardNow(),
        DEFAULT_BUSINESS_START_TIME,
      ),
      businessEnd: setDayFromWorkdayTimeString(
        getYardNow(),
        DEFAULT_BUSINESS_END_TIME,
      ),
    };
  }

  const minStartTimeStr = workdays.reduce(
    (min, w) =>
      compareWorkdayTimeStrings(w.startDayTime, min) < 0
        ? w.startDayTime
        : min,
    workdays[0].startDayTime || DEFAULT_BUSINESS_START_TIME,
  );
  const maxEndTimeStr = workdays.reduce(
    (max, w) =>
      compareWorkdayTimeStrings(w.endDayTime, max) > 0 ? w.endDayTime : max,
    workdays[0].endDayTime || DEFAULT_BUSINESS_END_TIME,
  );
  // Snap the grid anchor to the full hour so slot rows read 6:00, 7:00, …
  // even when the workday starts at e.g. 6:45. Events/timeslots position
  // relative to this anchor, so a 6:45 event renders 45min below the line.
  const businessStart = startOfHour(
    setDayFromWorkdayTimeString(getYardNow(), minStartTimeStr),
  );
  const businessEnd = setDayFromWorkdayTimeString(getYardNow(), maxEndTimeStr);

  return { businessStart, businessEnd };
};

// Helper function to generate hourly slots
const generateHourlySlots = (businessStart, businessEnd) => {
  let hourlySlots = [];
  let currentHour = businessStart;
  while (currentHour < businessEnd) {
    hourlySlots.push(format(currentHour, "h:mmaaa"));
    currentHour = add(currentHour, { hours: 1 });
  }
  return hourlySlots;
};

// Helper function to calculate day column height
const calculateDayColumnHeight = (hourlySlots) => {
  const totalSlots = hourlySlots.length;
  return totalSlots * SLOT_HEIGHT;
};

const getSlotPickLayouts = (
  day,
  eventType,
  businessStart,
  slotHeight,
  schedulingOverride,
) => {
  if (!day || !eventType) {
    return [];
  }

  const dayBusinessStart = applyWallClockFromDate(day, businessStart);
  const slotMinutes =
    Number(eventType.duration || 0) + Number(eventType.bufferTime || 0);
  const slotHeightPx =
    slotMinutes > 0 ? Math.max((slotMinutes / 60) * slotHeight, 12) : slotHeight;

  return getDayTimeslots(day, eventType, schedulingOverride).map((slotLabel) => {
    const slotDate = setDayFromWorkdayTimeString(day, slotLabel);
    const top =
      (differenceInMinutes(slotDate, dayBusinessStart) / 60) * slotHeight;

    return {
      slotLabel,
      top,
      height: slotHeightPx,
    };
  });
};

const useStyles = makeStyles((theme) => {
  const sideBorder = `1px solid ${
    theme.palette.type === "dark" ? borderColorDark : borderColorLight
  }`;

  const interactiveScheduleControl = {
    WebkitTapHighlightColor: "transparent",
    transition: theme.transitions.create(["box-shadow", "filter"], {
      duration: theme.transitions.duration.shorter,
    }),
    "&:hover": {
      boxShadow: theme.shadows[6],
      zIndex: 1,
      filter:
        theme.palette.type === "dark"
          ? "brightness(1.06)"
          : "brightness(0.97)",
    },
    "&:focus": {
      outline: "none",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
      zIndex: 2,
    },
    // No opacity dimming: disableClick cards are schedule *context* (e.g. on
    // the create/dispatch picker), not inactive controls — keep full contrast
    // and signal non-clickability via cursor + suppressed hover only.
    "&:disabled": {
      cursor: "default",
      filter: "none",
      boxShadow: "none",
    },
  };

  return {
    backdrop: {
      zIndex: 90001,
    },
    avatar: {
      width: 36,
      height: 36,
      backgroundColor: blue[600],
      color: "white",
    },

    // Shared panel frame (sides + horizontal margins)
    panelBase: {
      borderLeft: sideBorder,
      borderRight: sideBorder,
      marginLeft: 2,
      marginRight: 2,
    },

    navigationContainer: {
      backgroundColor:
        theme.palette.type === "dark" ? alpha(grey[700], 0.4) : grey[50],
      borderTop: sideBorder,
      padding: 4,
    },
    dayHeaderContainer: {
      backgroundColor:
        theme.palette.type === "dark" ? alpha(grey[900], 0.4) : grey[50],
      border: sideBorder,
      marginLeft: 2,
      marginRight: 2,
    },
    eventsContainer: {
      borderBottom: sideBorder,
      marginLeft: 2,
      marginRight: 2,
      borderLeft: sideBorder,
      borderRight: sideBorder,
    },

    monthButton: {
      "&:hover": {
        backgroundColor: "transparent",
        cursor: "auto",
      },
    },
    dialogContent: {
      overflow: "auto",
      maxHeight: "calc(100vh - 120px)",
      display: "flex",
      flexDirection: "column",
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    dayHeader: {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
      },
    },

    // Time label & grid
    timeLabelColumn: {
      display: "flex",
      width: `${100 - COLUMN_WIDTH}%`,
      flexDirection: "column",
      borderRight: sideBorder,
      height: "100%",
    },
    timeLabelCell: {
      height: SLOT_HEIGHT,
      minHeight: SLOT_HEIGHT,
      maxHeight: SLOT_HEIGHT,
      backgroundColor:
        theme.palette.type === "dark" ? alpha(grey[900], 0.4) : grey[50],
      borderBottom: sideBorder,
    },
    timeLabelText: {
      whiteSpace: "normal",
      wordWrap: "break-word",
      fontSize: "0.9rem",
      fontWeight: 500,
      marginLeft: 4,
    },
    gridLine: {
      position: "absolute",
      left: 0,
      right: 0,
      borderTop: sideBorder,
    },

    // Day columns
    dayColumnContainer: {
      display: "flex",
      justifyContent: "space-between",
      width: `${COLUMN_WIDTH}%`,
    },

    // Event containers: same visual language as Track Management railcar cards.
    eventBoxBase: {
      zIndex: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "center",
      overflow: "hidden",
      padding: theme.spacing(0.375, 0.75, 0.375, 1.5),
      borderRadius: theme.spacing(1),
      cursor: "pointer",
      position: "relative",
      fontFamily: theme.typography.fontFamily,
      fontSize: "inherit",
      lineHeight: 1.2,
      textAlign: "left",
      ...interactiveScheduleControl,
      "& *": {
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
        userSelect: "none",
        WebkitTouchCallout: "none",
      },
    },
    eventGroupBase: {
      zIndex: 2,
      position: "absolute",
      left: 4,
      right: 4,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(0.5),
      cursor: "pointer",
      overflow: "hidden",
      borderRadius: theme.spacing(1),
      padding: theme.spacing(0.5, 0.75),
      fontFamily: theme.typography.fontFamily,
      minHeight: MIN_EVENT_DISPLAY_HEIGHT,
      ...interactiveScheduleControl,
    },
    eventText: {
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      flexGrow: 1,
      minWidth: 0,
      lineHeight: 1.15,
      fontSize: "0.72rem",
    },
    eventStatusBadge: {
      flexShrink: 0,
      marginLeft: theme.spacing(0.75),
      padding: theme.spacing(0.125, 0.75),
      borderRadius: 999,
      fontSize: "0.6rem",
      fontWeight: 700,
      lineHeight: 1.5,
      letterSpacing: "0.02em",
      whiteSpace: "nowrap",
    },
    eventDirectionIcon: {
      flexShrink: 0,
      marginRight: 4,
      fontSize: "0.875rem",
    },
    eventAccountText: {
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: "0.65rem",
      lineHeight: 1.1,
      marginTop: 1,
    },
    eventTitleRow: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      minWidth: 0,
    },
    eventAccountIndicator: {
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 4,
      zIndex: 1,
    },
    eventProductIndicator: {
      position: "absolute",
      right: 0,
      top: 0,
      bottom: 0,
      width: 4,
      zIndex: 1,
    },
    // Clean card for the overlap popover list (grid blocks keep eventBoxBase).
    eventCardPopover: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(0.25),
      width: "100%",
      textAlign: "left",
      cursor: "pointer",
      position: "relative",
      overflow: "hidden",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(0.75, 1.5),
      marginBottom: theme.spacing(0.75),
      // Solid grey[800] surface (same as the grid cards) sitting on the darker
      // grey[900] popover paper, so each row reads as a real card and its
      // neutral accent bars (grey[700]) stay visible.
      backgroundColor:
        theme.palette.type === "dark"
          ? theme.palette.grey[800]
          : theme.palette.common.white,
      transition: theme.transitions.create(
        ["background-color", "border-color"],
        { duration: theme.transitions.duration.shortest },
      ),
      "&:last-child": { marginBottom: 0 },
      "&:hover:not(:disabled)": {
        borderColor: theme.palette.primary.main,
        backgroundColor:
          theme.palette.type === "dark"
            ? theme.palette.grey[600]
            : alpha(theme.palette.primary.main, 0.05),
      },
      "&:disabled": { cursor: "default" },
    },
    eventCardTitleRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
      minWidth: 0,
    },
    eventCardAccount: {
      fontSize: "0.68rem",
      lineHeight: 1.25,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    eventCardNumber: {
      flexGrow: 1,
      minWidth: 0,
      fontSize: "0.82rem",
      fontWeight: 600,
      lineHeight: 1.25,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    // background for a "disabled" / non-selected day column
    dayBgShaded: {
      backgroundColor:
        theme.palette.type === "dark" ? alpha(grey[700], 0.4) : grey[50],
    },

    // EventGroup box (mixed group)
    eventGroupBox: {
      border: `1px solid ${grey[600]}`,
      borderRadius: theme.spacing(1),
      backgroundColor:
        theme.palette.type === "dark" ? alpha(grey[900], 0.55) : grey[100],
    },
    eventGroupCountBadge: {
      marginRight: theme.spacing(0.75),
      minWidth: theme.spacing(2.75),
      height: theme.spacing(2.75),
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      borderRadius: theme.spacing(1.25),
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "0.75rem",
      fontWeight: theme.typography.fontWeightBold,
      lineHeight: 1,
      color:
        theme.palette.type === "dark"
          ? theme.palette.common.white
          : theme.palette.grey[900],
      backgroundColor:
        theme.palette.type === "dark"
          ? alpha(theme.palette.primary.light, 0.35)
          : alpha(theme.palette.primary.main, 0.22),
      border: `1px solid ${
        theme.palette.type === "dark"
          ? alpha(theme.palette.primary.light, 0.5)
          : alpha(theme.palette.primary.dark, 0.28)
      }`,
    },
    // Link glyph on an overlap pill: a collapsed hour that hides a linked
    // truck still signals it (AD-12). Same deepPurple as ShipsTogetherBadge.
    eventGroupLinkIcon: {
      display: "inline-flex",
      alignItems: "center",
      flexShrink: 0,
      marginRight: theme.spacing(0.75),
      fontSize: "1rem",
      color: getShipsTogetherBadgeStyle(theme).color,
    },
    eventGroupSummary: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "center",
      textAlign: "left",
    },
    eventGroupExpandIcon: {
      flexShrink: 0,
      fontSize: "1.35rem",
      color: theme.palette.text.secondary,
      marginLeft: theme.spacing(-0.25),
    },

    // Hover tooltip for calendar events (structured, bounded width)
    eventTooltipPaper: {
      maxWidth: 288,
      padding: theme.spacing(1.25, 1.5),
      boxSizing: "border-box",
    },
    eventTooltipInner: {
      textAlign: "left",
      color: theme.palette.common.white,
    },
    eventTooltipMeta: {
      fontSize: "0.72rem",
      lineHeight: 1.35,
      opacity: 0.88,
      marginBottom: theme.spacing(0.5),
    },
    eventTooltipPrimary: {
      fontSize: "0.875rem",
      fontWeight: theme.typography.fontWeightBold,
      lineHeight: 1.35,
      wordBreak: "break-word",
    },
    eventTooltipRow: {
      marginTop: theme.spacing(0.75),
    },
    eventTooltipFieldLabel: {
      fontSize: "0.65rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      opacity: 0.72,
      marginBottom: theme.spacing(0.25),
    },
    eventTooltipFieldValue: {
      fontSize: "0.75rem",
      lineHeight: 1.38,
      opacity: 0.95,
      wordBreak: "break-word",
    },
    eventTooltipStatus: {
      fontSize: "0.74rem",
      fontWeight: theme.typography.fontWeightBold,
      marginTop: theme.spacing(1),
      paddingTop: theme.spacing(0.75),
      borderTop: `1px solid ${alpha(theme.palette.common.white, 0.22)}`,
      wordBreak: "break-word",
    },

    dayClusterButton: {
      zIndex: 2,
      position: "absolute",
      left: 8,
      right: 8,
      minHeight: MIN_EVENT_DISPLAY_HEIGHT,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.spacing(1),
      border: `1px dashed ${grey[600]}`,
      backgroundColor:
        theme.palette.type === "dark" ? alpha(grey[900], 0.34) : grey[50],
      color: theme.palette.text.primary,
      cursor: "pointer",
      fontFamily: theme.typography.fontFamily,
      textAlign: "left",
      ...interactiveScheduleControl,
    },
    dividerLeft: {
      borderLeft: sideBorder,
    },
    dividerRight: {
      borderRight: sideBorder,
    },

    slotPickCell: {
      position: "absolute",
      left: 0,
      right: 0,
      padding: 0,
      margin: 0,
      border: "none",
      backgroundColor: "transparent",
      cursor: "pointer",
      zIndex: 0,
      WebkitTapHighlightColor: "transparent",
      transition: theme.transitions.create(["background-color"], {
        duration: theme.transitions.duration.shorter,
      }),
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
      },
      "&:focus": {
        outline: "none",
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: -1,
        zIndex: 1,
      },
    },
    slotPickCellUnavailable: {
      cursor: "not-allowed",
      opacity: 0.45,
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  };
});

const getEventsByYard = async (
  uniqueWeekPairs,
  { isCancelled = () => false } = {},
) => {
  const weekPagePromises = Array.from(uniqueWeekPairs.values()).map((pair) =>
    fetchAllGraphqlPages({
      query: timeslotByYearByWeek,
      variables: {
        reloadYardId: window?.appUser?.reloadYardId,
        yearWeek: {
          eq: {
            year: pair.year,
            week: pair.week,
          },
        },
        limit: 1000,
      },
      selectPage: (response) => {
        const result = response.data?.timeslotByYearByWeek;
        return { items: result?.items, token: result?.nextToken };
      },
      isCancelled,
    }),
  );
  const weekPages = await Promise.all(weekPagePromises);
  if (weekPages.some((page) => page === null)) {
    return null;
  }
  return weekPages.flat();
};

const getDayName = (date) => formatYardScheduleDate(date, "EEE");

// Pending empty-slot picks have no id until saved — fall back to their stable
// key so React never renders an undefined list key.
const getEventRenderKey = (event) =>
  event.id || getSelectedTimeslotStableKey(event);

const getSelectedTimeslotStableKey = (timeslot) => {
  if (!timeslot) return null;
  const eventType = timeslot.eventType || {};
  const capacityBucketId =
    eventType.assetTypeId ||
    eventType.assetType?.id ||
    timeslot.eventTypeId ||
    eventType.id ||
    "";
  return [
    timeslot.year,
    timeslot.week,
    timeslot.month,
    timeslot.day,
    timeslot.time ?? "",
    capacityBucketId,
  ].join("#");
};

// Identity match, not slot equality: another account's booking in the same
// slot (capacity >= 2) must not swallow the user's own id-less pending pick,
// so an id-less pending timeslot is always appended — except when the pending
// pick re-selects the user's OWN persisted booking (ownTimeslotId at the same
// slot key), which would double-render one booking.
export const isTimeslotListedInEvents = (events, timeslot, ownTimeslotId) =>
  events.some(
    (event) =>
      (event.id && timeslot?.id && event.id === timeslot.id) ||
      (event.id &&
        ownTimeslotId &&
        event.id === ownTimeslotId &&
        getSelectedTimeslotStableKey(event) ===
          getSelectedTimeslotStableKey(timeslot)),
  );

// Helper: Filter events for the day and group overlapping ones.
const getEventsForDay = (allEvents, day) => {
  const dayParts = getYardCalendarDateParts(day);
  const eventsForDay = allEvents.filter((e) => {
    return (
      e.year === dayParts.year &&
      e.month === dayParts.month &&
      e.day === dayParts.day
    );
  });
  return groupOverlappingEvents(eventsForDay, day);
};

// Compute 7 consecutive days starting from weekStartDate.
// For each day, match the schedule object (by day-of-week) to preserve formatting.
const getComputedWeekDays = (weekStartDate, eventType, yardSchedule) => {
  if (!weekStartDate) return [];

  const weekWindowStart = startOfDay(weekStartDate);
  const weekWindowEnd = startOfDay(add(weekStartDate, { days: 6 }));
  const holidayDayKeySet = new Set(
    resolveHolidayRulesToBlockedDates(
      coerceFormArrayField(yardSchedule?.holidays),
      weekWindowStart,
      weekWindowEnd,
      usHolidays,
    ).map((d) => calendarDayKey(d)),
  );

  return Array.from({ length: 7 }, (_, i) => {
    const date = add(weekStartDate, { days: i });

    // Start with yardSchedule as the base workdays
    let workdays = yardSchedule?.workdays ? [...yardSchedule.workdays] : [];

    // Iterate over eventType's workdays and update workdays accordingly
    eventType?.schedule?.workdays?.forEach((eventDay) => {
      const dayIndex = workdays.findIndex((w) => w.id === eventDay.id);
      if (dayIndex !== -1) {
        // If the eventType explicitly disables the day, update it in workdays
        if (eventDay.selected === false) {
          workdays[dayIndex] = { ...workdays[dayIndex], selected: false };
        }
      }
    });

    let scheduleForDay = workdays.find(
      (w) => w.id === String(getYardCalendarDayOfWeek(date))
    ) || { selected: true };

    if (holidayDayKeySet.has(calendarDayKey(date))) {
      scheduleForDay = { ...scheduleForDay, selected: false };
    }

    return { date, schedule: scheduleForDay };
  });
};

/** Collect all pages for an AppSync list-style query (nextToken loop). Returns null if cancelled mid-flight. */
const fetchAllGraphqlPages = async ({
  query,
  variables,
  selectPage,
  isCancelled,
}) => {
  const rows = [];
  let nextToken = null;
  do {
    const response = await API.graphql(
      graphqlOperation(query, { ...variables, nextToken }),
    );
    if (isCancelled()) return null;
    const { items, token } = selectPage(response);
    rows.push(...(items || []));
    nextToken = token || null;
  } while (nextToken);
  return rows;
};

export const EventsCalendar = ({
  eventType,
  selectedTimeslot,
  // Id of the still-persisted booking being rescheduled; selectedTimeslot
  // loses its id after an unsaved re-pick, but capacity checks must keep
  // excluding the original booking.
  persistedTimeslotId,
  disableClick,
  height = "100%",
  hideNavButtons = false,
  onTimeslotPick,
  account,
  schedulingOverride = false,
}) => {
  const notify = useNotify();
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [allEvents, setAllEvents] = useState([]); // raw events from GraphQL
  const [selectedEvent, setSelectedEvent] = useState();
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [isDayView, setIsDayView] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const loadedRef = useRef(false);
  useEffect(() => {
    loadedRef.current = loaded;
  }, [loaded]);

  const fetchRequestRef = useRef(0);
  const pickRequestRef = useRef(0);
  const selectedTimeslotRef = useRef(selectedTimeslot);
  selectedTimeslotRef.current = selectedTimeslot;
  const pendingTimeslotStableKeyRef = useRef(null);
  const selectedTimeslotStableKey =
    getSelectedTimeslotStableKey(selectedTimeslot);

  useEffect(
    () => () => {
      fetchRequestRef.current += 1;
      pickRequestRef.current += 1;
    },
    [],
  );

  const yardSchedule = window?.appUser?.reloadYard?.schedule;
  const layout = yardSchedule?.layout || CalendarLayout.TODAY_VIEW;
  const reloadYardId = window?.appUser?.reloadYardId ?? null;
  const [weekStartDate, setWeekStartDate] = useState(() => {
    const now = getYardNow();
    return smDown || layout === CalendarLayout.TODAY_VIEW
      ? now
      : startOfWeek(now);
  });
  const [workdays, setWorkdays] = useState(yardSchedule?.workdays || []);
  const schedulingEventType = useMemo(
    () =>
      initializeSchedulingState(selectedTimeslot, eventType, schedulingOverride)
        .initializedEventType,
    [eventType, schedulingOverride, selectedTimeslot],
  );
  const computedWeekDays = useMemo(
    () => getComputedWeekDays(weekStartDate, eventType, yardSchedule),
    [weekStartDate, eventType, yardSchedule],
  );
  const [, setColorMapsVersion] = useState(0);

  const { businessStart, hourlySlots, dayColumnHeight } = useMemo(() => {
    const { businessStart: bs, businessEnd: be } =
      calculateBusinessHours(workdays);
    const slots = generateHourlySlots(bs, be);
    return {
      businessStart: bs,
      hourlySlots: slots,
      dayColumnHeight: calculateDayColumnHeight(slots),
    };
  }, [workdays]);

  useEffect(() => {
    if (!reloadYardId) return undefined;

    let cancelled = false;
    const isCancelled = () => cancelled;

    (async () => {
      await Promise.all([
        (async () => {
          try {
            const accounts = await fetchAllGraphqlPages({
              query: accountColorsByActiveByName,
              variables: {
                reloadYardId,
                activeBusinessName: { beginsWith: { active: "true" } },
                limit: 1000,
              },
              selectPage: (response) => {
                const result = response.data?.accountByActiveByName;
                return { items: result?.items, token: result?.nextToken };
              },
              isCancelled,
            });
            if (accounts === null) return;
            const colorMap = {};
            accounts.forEach((account) => {
              if (account?.id && account.color)
                colorMap[account.id] = account.color;
            });
            setAccountColorMap(colorMap);
          } catch (err) {
            logError("EventsCalendar:accountColors", err, notify);
          }
        })(),
        (async () => {
          try {
            const products = await fetchAllGraphqlPages({
              query: productColorsByReloadYard,
              variables: {
                filter: {
                  reloadYardId: { eq: reloadYardId },
                  active: { eq: "true" },
                },
                limit: 1000,
              },
              selectPage: (response) => {
                const result = response.data?.listProducts;
                return { items: result?.items, token: result?.nextToken };
              },
              isCancelled,
            });
            if (products === null) return;
            const colorMap = {};
            products.forEach((product) => {
              if (product?.id && product.color)
                colorMap[product.id] = product.color;
            });
            setProductColorMap(colorMap, products);
          } catch (err) {
            logError("EventsCalendar:productColors", err, notify);
          }
        })(),
      ]);
      if (!cancelled) {
        setColorMapsVersion((version) => version + 1);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [notify, reloadYardId]);

  useEffect(() => {
    if (eventType?.schedule?.workdays) {
      setWorkdays(eventType.schedule.workdays);
    } else {
      setWorkdays(yardSchedule?.workdays || []); // Fallback to yard workdays
    }
  }, [eventType]); // eslint-disable-line react-hooks/exhaustive-deps -- legacy: only eventType should refresh workdays

  const fetchEvents = useCallback(async () => {
    const requestId = fetchRequestRef.current + 1;
    fetchRequestRef.current = requestId;
    try {
      !loadedRef.current && setLoading(true);
      const uniqueWeekPairs = new Map();
      computedWeekDays.forEach(({ date }) => {
        const { year, week } = getYardCalendarDateParts(date);
        uniqueWeekPairs.set(`${year}-${week}`, { year, week });
      });

      const events = await getEventsByYard(uniqueWeekPairs, {
        isCancelled: () => requestId !== fetchRequestRef.current,
      });
      if (requestId !== fetchRequestRef.current || events === null) return;

      const pendingTimeslot = selectedTimeslotRef.current;
      const nextEvents =
        pendingTimeslot &&
        !isTimeslotListedInEvents(events, pendingTimeslot, persistedTimeslotId)
          ? [...events, pendingTimeslot]
          : events;
      setAllEvents(nextEvents);
    } catch (err) {
      if (requestId === fetchRequestRef.current) {
        logError("EventsCalendar:getData", err, notify);
      }
    } finally {
      if (requestId === fetchRequestRef.current) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [computedWeekDays, notify, persistedTimeslotId]);

  useEffect(() => {
    const pendingTimeslot = selectedTimeslotRef.current;
    const previousPendingKey = pendingTimeslotStableKeyRef.current;
    pendingTimeslotStableKeyRef.current = selectedTimeslotStableKey;

    setAllEvents((events) => {
      const eventsWithoutPreviousPending = previousPendingKey
        ? events.filter(
            (event) =>
              event.id ||
              getSelectedTimeslotStableKey(event) !== previousPendingKey,
          )
        : events;

      if (!pendingTimeslot) return eventsWithoutPreviousPending;
      return isTimeslotListedInEvents(
        eventsWithoutPreviousPending,
        pendingTimeslot,
        persistedTimeslotId,
      )
        ? eventsWithoutPreviousPending
        : [...eventsWithoutPreviousPending, pendingTimeslot];
    });
  }, [selectedTimeslotStableKey, persistedTimeslotId]);

  // keep visible timeslots in sync
  useEffect(() => {
    if (!computedWeekDays || computedWeekDays.length === 0) return;
    if (!reloadYardId) return;
    if (!loaded) return;

    const intervalId = setInterval(() => {
      fetchEvents();
    }, REFRESH_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [computedWeekDays, loaded, fetchEvents, reloadYardId]);

  // Initialize to scheduled date (selectedTimeslot if available, else current date)
  const initScheduled = () => {
    let date = getYardNow();
    if (selectedTimeslot) {
      date = set(getYardNow(), {
        year: selectedTimeslot.year,
        month: selectedTimeslot.month,
        date: selectedTimeslot.day,
      });
      setSelectedEvent(selectedTimeslot);
    }

    if (smDown || layout === CalendarLayout.TODAY_VIEW) {
      // TODAY_VIEW: start with the current date as the first column
      setWeekStartDate(date);
    } else {
      setWeekStartDate(startOfWeek(date));
    }
  };

  // Initialize to current date (ignoring any selectedTimeslot)
  const initToday = () => {
    const date = getYardNow();
    if (smDown || layout === CalendarLayout.TODAY_VIEW) {
      // TODAY_VIEW: start with the current date as the first column
      setWeekStartDate(date);
    } else {
      setWeekStartDate(startOfWeek(date));
    }
  };

  useEffect(() => {
    if (computedWeekDays.length > 0) {
      fetchEvents();
    }
  }, [computedWeekDays, fetchEvents]);

  useEffect(() => {
    initScheduled();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only seed from selectedTimeslot / layout

  const handleEmptySlotPick = useCallback(
    async (date, slotTimeLabel) => {
      if (!onTimeslotPick) return;
      if (!schedulingEventType?.id) {
        notify("Choose an event type before picking a time on the calendar.", {
          type: "warning",
        });
        return;
      }
      const reloadYardId =
        account?.reloadYardId || window.appUser?.reloadYardId || null;
      if (!reloadYardId) {
        notify(
          "Yard context is missing; cannot schedule from the calendar right now.",
          { type: "warning" },
        );
        return;
      }
      if (shouldDisableDate(schedulingEventType, date, schedulingOverride)) {
        notify("That day is not available for scheduling.", {
          type: "warning",
        });
        return;
      }
      const tempTimeslot = {
        groups: account?.groups,
        status: EventType.RESERVED,
        reloadYardId,
        accountId: account?.id,
        accountName: account?.businessName,
        eventTypeId: schedulingEventType.id,
        eventType: schedulingEventType,
        ...getYardCalendarDateParts(date),
        time: slotTimeLabel,
      };
      const requestId = pickRequestRef.current + 1;
      pickRequestRef.current = requestId;
      try {
        // Always validate against fresh server data (double-booking fix);
        // local allEvents can be stale.
        const unavailable = await validateAppointment(
          tempTimeslot,
          selectedTimeslot?.id || persistedTimeslotId,
        );
        if (requestId !== pickRequestRef.current) return;
        if (unavailable) {
          notify(`${slotTimeLabel} is no longer available.`, {
            type: "warning",
          });
          return;
        }
        onTimeslotPick(tempTimeslot);
      } catch (err) {
        if (requestId === pickRequestRef.current) {
          logError("EventsCalendar:emptySlotPick", err, notify);
          notify("Could not verify that time slot. Try again.", {
            type: "warning",
          });
        }
      }
    },
    [
      account,
      notify,
      onTimeslotPick,
      persistedTimeslotId,
      schedulingEventType,
      schedulingOverride,
      selectedTimeslot,
    ],
  );

  const handleClick = (event) => {
    if (disableClick) return;
    const path = event.inboundId
      ? `/inboundShipments/${event.inboundId}`
      : event.outboundId
      ? `/outboundShipments/${event.outboundId}`
      : `/eventDetails/${event.id}`;
    deepRedirect(history, path);
  };

  const selectedDayScheduleForDayView = useMemo(() => {
    if (!selectedDay) return null;
    const row = computedWeekDays.find((w) =>
      isSameYardScheduleDay(w.date, selectedDay),
    );
    return row?.schedule ?? null;
  }, [computedWeekDays, selectedDay]);

  if (!weekStartDate) return <></>;
  return (
    <Box display="flex" alignItems="center">
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {isDayView && selectedDay ? (
        <DayView
          selectedDay={selectedDay}
          allEvents={allEvents}
          classes={classes}
          handleClick={handleClick}
          disableClick={disableClick}
          setIsDayView={setIsDayView}
          setSelectedDay={setSelectedDay}
          hourlySlots={hourlySlots}
          dayColumnHeight={dayColumnHeight}
          businessStart={businessStart}
          eventType={schedulingEventType}
          onTimeslotPick={onTimeslotPick}
          handleEmptySlotPick={handleEmptySlotPick}
          selectedDaySchedule={selectedDayScheduleForDayView}
          schedulingOverride={schedulingOverride}
        />
      ) : (
        <WeekView
          setIsDayView={setIsDayView}
          setSelectedDay={setSelectedDay}
          selectedEvent={selectedEvent}
          classes={classes}
          computedWeekDays={computedWeekDays}
          height={height}
          hideNavButtons={hideNavButtons}
          weekStartDate={weekStartDate}
          setWeekStartDate={setWeekStartDate}
          smDown={smDown}
          initScheduled={initScheduled}
          initToday={initToday}
          allEvents={allEvents}
          handleClick={handleClick}
          disableClick={disableClick}
          hourlySlots={hourlySlots}
          dayColumnHeight={dayColumnHeight}
          businessStart={businessStart}
          eventType={schedulingEventType}
          onTimeslotPick={onTimeslotPick}
          handleEmptySlotPick={handleEmptySlotPick}
          schedulingOverride={schedulingOverride}
        />
      )}
    </Box>
  );
};

// Day View Component
const DayView = ({
  selectedDay,
  allEvents,
  classes,
  handleClick,
  disableClick,
  setIsDayView,
  setSelectedDay,
  hourlySlots,
  dayColumnHeight,
  businessStart,
  eventType,
  onTimeslotPick,
  handleEmptySlotPick,
  selectedDaySchedule,
  schedulingOverride,
}) => {
  const dayEvents = useMemo(
    () => getEventsForDay(allEvents, selectedDay),
    [allEvents, selectedDay],
  );
  const dayEventsColumnRef = useRef(null);
  const [dayEventsColumnWidth, setDayEventsColumnWidth] = useState();
  const slotPickLayouts = useMemo(
    () =>
      onTimeslotPick && eventType?.id
        ? getSlotPickLayouts(
            selectedDay,
            eventType,
            businessStart,
            SLOT_HEIGHT,
            schedulingOverride,
          )
        : [],
    [
      businessStart,
      eventType,
      onTimeslotPick,
      schedulingOverride,
      selectedDay,
    ],
  );
  const dayViewClusterThreshold = useMemo(
    () => getResponsiveDayViewClusterThreshold(dayEventsColumnWidth),
    [dayEventsColumnWidth],
  );

  useEffect(() => {
    const node = dayEventsColumnRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      const nextWidth = node.clientWidth || node.getBoundingClientRect().width || 0;
      setDayEventsColumnWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth,
      );
    };

    updateWidth();

    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver(() => {
        updateWidth();
      });
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Handle back to week view
  const handleBackToWeek = () => {
    setIsDayView(false);
    setSelectedDay(null);
  };

  return (
    <Grid container>
      {/* Day View Header */}
      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="space-between"
          className={`${classes.panelBase} ${classes.navigationContainer}`}
        >
          <Button
            onClick={handleBackToWeek}
            startIcon={<ArrowBackIosIcon />}
            color="default"
          >
            Back to Week
          </Button>
          <Typography variant="h6">
            {formatYardScheduleDate(selectedDay, "EEEE, MMMM d, yyyy")}
          </Typography>
          <Box />
        </Box>
      </Grid>

      {/* Day View Events */}
      <Grid item xs={12} style={{ flex: 1, display: "flex" }}>
        <Box
          display="flex"
          overflow="auto"
          height="100%"
          className={classes.eventsContainer}
          style={{ flex: 1 }}
        >
          {/* Left Column: Hourly Time Labels */}
          <Box className={classes.timeLabelColumn}>
            {hourlySlots.map((slot, idx) => (
              <Box key={idx} className={classes.timeLabelCell}>
                <Typography variant="caption" className={classes.timeLabelText}>
                  {slot}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Right Column: Events */}
          <Box
            display="flex"
            width="100%"
            position="relative"
            ref={dayEventsColumnRef}
            style={{
              height: dayColumnHeight,
            }}
          >
            {/* Hourly grid lines */}
            {hourlySlots.map((_, i) => (
              <Box
                key={i}
                className={classes.gridLine}
                style={{ top: i * SLOT_HEIGHT }}
              />
            ))}
            {onTimeslotPick &&
              eventType?.id &&
              slotPickLayouts.map(({ slotLabel, top, height }, i) => {
                const dayDisabled = isEmptySlotDayDisabled({
                  eventType,
                  day: selectedDay,
                  daySchedule: selectedDaySchedule,
                  schedulingOverride,
                  shouldDisableDateForSchedule: shouldDisableDate,
                });
                return (
                  <button
                    key={`slot-pick-${slotLabel}-${i}`}
                    type="button"
                    className={`${classes.slotPickCell}${
                      dayDisabled ? ` ${classes.slotPickCellUnavailable}` : ""
                    }`}
                    style={{ top, height }}
                    disabled={dayDisabled}
                    aria-label={`Schedule ${slotLabel} on ${formatYardScheduleDate(
                      selectedDay,
                      "EEEE, MMM d",
                    )}`}
                    title={
                      dayDisabled
                        ? undefined
                        : "Choose this time for the appointment"
                    }
                    onClick={() => {
                      if (dayDisabled) return;
                      void handleEmptySlotPick(selectedDay, slotLabel);
                    }}
                  />
                );
              })}
            {/* Day View Events */}
            <DayViewEvents
              day={selectedDay}
              groups={dayEvents}
              businessStart={businessStart}
              slotHeight={SLOT_HEIGHT}
              clusterThreshold={dayViewClusterThreshold}
              handleClick={handleClick}
              disableClick={disableClick}
            />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

// Week View Component
const WeekView = ({
  setIsDayView,
  setSelectedDay,
  selectedEvent,
  classes,
  computedWeekDays,
  height,
  hideNavButtons,
  weekStartDate,
  setWeekStartDate,
  smDown,
  initScheduled,
  initToday,
  allEvents,
  handleClick,
  disableClick,
  hourlySlots,
  dayColumnHeight,
  businessStart,
  eventType,
  onTimeslotPick,
  handleEmptySlotPick,
  schedulingOverride,
}) => {
  const weekColumns = useMemo(
    () =>
      computedWeekDays.map((wd) => ({
        ...wd,
        groups: getEventsForDay(allEvents, wd.date),
        slotPickLayouts:
          onTimeslotPick && eventType?.id
            ? getSlotPickLayouts(
                wd.date,
                eventType,
                businessStart,
                SLOT_HEIGHT,
                schedulingOverride,
              )
            : [],
      })),
    [
      allEvents,
      businessStart,
      computedWeekDays,
      eventType,
      onTimeslotPick,
      schedulingOverride,
    ],
  );

  // Handle day header click to switch to day view
  const handleDayClick = (day) => {
    // Switch to day view for the clicked day
    setIsDayView(true);
    setSelectedDay(day);
  };

  return (
    <Grid container>
      {/* --- Top Controls (Navigation) --- */}
      {!hideNavButtons && (
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            className={`${classes.panelBase} ${classes.navigationContainer}`}
          >
            <Navigation
              classes={classes}
              changeWeek={(days) =>
                setWeekStartDate(add(weekStartDate, { days }))
              }
              weekStartDate={weekStartDate}
              smDown={smDown}
              initScheduled={initScheduled}
              initToday={initToday}
            />
          </Box>
        </Grid>
      )}

      {/* --- Week View Day Headers --- */}
      <Grid item xs={12}>
        <Box display="flex" className={classes.dayHeaderContainer}>
          {/* Left spacer to align with time labels */}
          <Box
            display="flex"
            width={`${100 - COLUMN_WIDTH}%`}
            flexDirection="column"
            component="span"
            className={classes.dividerRight}
          />
          <Box className={classes.dayColumnContainer}>
            {computedWeekDays.map((wd, idx) => (
              <DayHeader
                key={idx}
                weekDay={wd.schedule}
                day={wd.date}
                selectedEvent={selectedEvent}
                classes={classes}
                onClick={() => handleDayClick(wd.date)}
              />
            ))}
          </Box>
        </Box>
      </Grid>

      {/* --- Scrollable Events Area --- */}
      <Grid item xs={12}>
        <Box
          display="flex"
          overflow="auto"
          className={classes.eventsContainer}
          style={
            height === "auto"
              ? { minHeight: dayColumnHeight, height: "auto" }
              : { height }
          }
        >
          {/* Left Column: Hourly Time Labels */}
          <Box className={classes.timeLabelColumn}>
            {hourlySlots.map((slot, idx) => (
              <Box key={idx} className={classes.timeLabelCell}>
                <Typography variant="caption" className={classes.timeLabelText}>
                  {slot}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Right Column: Day Columns */}
          <Box className={classes.dayColumnContainer}>
            {weekColumns.map((wd, idx) => {
              const groupedEvents = wd.groups;
              return (
                <Box
                  key={idx}
                  flex={1}
                  className={[
                    !wd.schedule || wd.schedule.selected
                      ? ""
                      : classes.dayBgShaded,
                    classes.dividerLeft,
                  ]
                    .join(" ")
                    .trim()}
                  style={{ height: dayColumnHeight }}
                >
                  {/* Container for absolute event placement */}
                  <Box
                    position="relative"
                    style={{
                      height: dayColumnHeight,
                      borderTop: "0px",
                      marginTop: "-1px",
                    }}
                  >
                    {/* Hourly grid lines */}
                    {hourlySlots.map((_, i) => (
                      <Box
                        key={i}
                        className={classes.gridLine}
                        style={{ top: i * SLOT_HEIGHT }}
                      />
                    ))}
                    {onTimeslotPick &&
                      eventType?.id &&
                      wd.slotPickLayouts.map(({ slotLabel, top, height }, i) => {
                        const dayDisabled = isEmptySlotDayDisabled({
                          eventType,
                          day: wd.date,
                          daySchedule: wd.schedule,
                          schedulingOverride,
                          shouldDisableDateForSchedule: shouldDisableDate,
                        });
                        return (
                          <button
                            key={`slot-pick-${idx}-${slotLabel}-${i}`}
                            type="button"
                            className={`${classes.slotPickCell}${
                              dayDisabled
                                ? ` ${classes.slotPickCellUnavailable}`
                                : ""
                            }`}
                            style={{ top, height }}
                            disabled={dayDisabled}
                            title={
                              dayDisabled
                                ? undefined
                                : "Choose this time for the appointment"
                            }
                            aria-label={`Schedule ${slotLabel} on ${formatYardScheduleDate(
                              wd.date,
                              "EEE MMM d",
                            )}`}
                            onClick={() => {
                              if (dayDisabled) return;
                              void handleEmptySlotPick(wd.date, slotLabel);
                            }}
                          />
                        );
                      })}
                    {/* Render grouped events via the Events component */}
                    <Events
                      day={wd.date}
                      groups={groupedEvents}
                      businessStart={businessStart}
                      slotHeight={SLOT_HEIGHT}
                      handleClick={handleClick}
                      disableClick={disableClick}
                      smDown={smDown}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

// Navigation Component
const Navigation = ({
  classes,
  changeWeek,
  weekStartDate,
  smDown,
  initScheduled,
  initToday,
}) => {
  return (
    <>
      <span>
        <IconButton
          onClick={() => changeWeek(smDown ? -1 : -7)}
          color="default"
        >
          <ArrowBackIosIcon fontSize="small" />
        </IconButton>
        <Button
          size="large"
          disableRipple
          disableElevation
          disableFocusRipple
          className={classes.monthButton}
        >
          {formatYardScheduleDate(weekStartDate, "MMMM yyyy")}
        </Button>
        <IconButton onClick={() => changeWeek(smDown ? 1 : 7)} color="default">
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
        <>
          <Button
            onClick={() => initScheduled()}
            startIcon={<TodayOutlinedIcon />}
            style={{ marginRight: 8 }}
            color="default"
          >
            Scheduled
          </Button>
          <Button
            onClick={() => initToday()}
            startIcon={<TodayOutlinedIcon />}
            style={{ marginRight: 8 }}
            color="primary"
          >
            Today
          </Button>
        </>
      </span>
      {!smDown && (
        <IconButton onClick={() => changeWeek(0)} color="default">
          <RefreshIcon fontSize="small" />
        </IconButton>
      )}
    </>
  );
};

// DayHeader Component
const DayHeader = ({ weekDay, day, selectedEvent, classes, onClick }) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width="100%"
      pt={1.0}
      pb={1.0}
      className={classes.dayHeader}
      onClick={onClick}
    >
      <Box display="flex" justifyContent="center">
        <Typography
          component="span"
          variant="body2"
          color={weekDay && weekDay.selected ? "textSecondary" : "error"}
          style={{ paddingBottom: 4 }}
        >
          {getDayName(day)}
        </Typography>
        {selectedEvent &&
          selectedEvent.day === getYardCalendarDateParts(day).day && (
            <StarIcon
              color="error"
              style={{ fontSize: 16, margin: 2, marginRight: 4 }}
            />
          )}
      </Box>
      {isSameYardScheduleDay(day, getYardNow()) ? (
        <Avatar className={classes.avatar}>
          {formatYardScheduleDate(day, "d")}
        </Avatar>
      ) : (
        <Typography
          component="span"
          variant="h6"
          color={weekDay && weekDay.selected ? "textPrimary" : "error"}
        >
          {formatYardScheduleDate(day, "d")}
        </Typography>
      )}
    </Box>
  );
};

// Day View Events Component
const DayViewEvents = ({
  day,
  groups,
  businessStart,
  slotHeight,
  clusterThreshold,
  handleClick,
  disableClick,
}) => {
  // Calculate the day's business start (using the businessStart time)
  const dayBusinessStart = applyWallClockFromDate(day, businessStart);

  return (
    <>
      {groups.map((group, idx) => {
        const top =
          (differenceInMinutes(group.groupStart, dayBusinessStart) / 60) *
          slotHeight;
        const groupHeight =
          (differenceInMinutes(group.groupEnd, group.groupStart) / 60) *
          slotHeight;

        if (group.events.length === 1) {
          const height = getSingleEventRenderHeight(
            group.events[0],
            groupHeight,
            slotHeight,
          );
          return (
            <SingleEvent
              key={getEventRenderKey(group.events[0])}
              event={group.events[0]}
              top={top}
              height={height}
              handleClick={handleClick}
              disableClick={disableClick}
              compact={height < 36}
            />
          );
        }

        if (group.events.length >= clusterThreshold) {
          // Span the full overlap window (first start → latest end), same as
          // the week-view pill; minHeight floors very short windows.
          const clusterHeight = Math.max(MIN_EVENT_DISPLAY_HEIGHT, groupHeight);
          return (
            <DayViewEventCluster
              key={`cluster-${idx}`}
              group={group}
              top={top}
              height={clusterHeight}
              handleClick={handleClick}
              disableClick={disableClick}
            />
          );
        }

        const laneLayouts = buildDayViewLaneLayout(
          group.events,
          day,
          group.groupStart,
          slotHeight,
        );
        const maxBottom = laneLayouts.reduce(
          (max, layout) => Math.max(max, layout.top + layout.height),
          groupHeight,
        );
        const overlapGroupHeight = Math.max(groupHeight, maxBottom);

        return (
          <Box
            key={`group-${idx}`}
            position="absolute"
            top={top}
            left={8}
            right={8}
            height={overlapGroupHeight}
          >
            {laneLayouts.map((layout) => {
              return (
                <SingleEvent
                  key={getEventRenderKey(layout.event)}
                  event={layout.event}
                  width={layout.width}
                  left={layout.left}
                  top={layout.top}
                  height={layout.height}
                  handleClick={handleClick}
                  disableClick={disableClick}
                  compact={layout.height < 36}
                  inPopover={false}
                />
              );
            })}
          </Box>
        );
      })}
    </>
  );
};

const Events = ({
  day,
  groups,
  businessStart,
  slotHeight,
  handleClick,
  disableClick,
  smDown,
}) => {
  // Calculate the day’s business start (using the businessStart time)
  const dayBusinessStart = applyWallClockFromDate(day, businessStart);

  return (
    <>
      {groups.map((group, idx) => {
        const top =
          (differenceInMinutes(group.groupStart, dayBusinessStart) / 60) *
          slotHeight;
        const groupHeight =
          (differenceInMinutes(group.groupEnd, group.groupStart) / 60) *
          slotHeight;

        // Render a single event or a group block
        if (group.events.length === 1) {
          const height = getSingleEventRenderHeight(
            group.events[0],
            groupHeight,
            slotHeight,
          );
          return (
            <SingleEvent
              key={getEventRenderKey(group.events[0])}
              event={group.events[0]}
              top={top}
              height={height}
              handleClick={handleClick}
              disableClick={disableClick}
              compact={height < 36}
            />
          );
        }

        // Week columns are too narrow for side-by-side lanes (day view has
        // those) — any overlapping window collapses into one expander pill.
        return (
          <EventGroup
            key={`group-${idx}`}
            group={group}
            top={top}
            height={groupHeight}
            handleClick={handleClick}
            disableClick={disableClick}
            smDown={smDown}
          />
        );
      })}
    </>
  );
};

const INBOUND_STATUS_LABEL_BY_ID = new Map(
  (InboundStatuses || []).map((row) => [row.id, row.name]),
);

const getScheduleShipmentStatusLabel = (event) => {
  const shipment = event.inboundShipment || event.outboundShipment;
  const raw = shipment?.status;
  if (!raw) return null;
  if (event.inboundId) {
    return INBOUND_STATUS_LABEL_BY_ID.get(raw) || raw;
  }
  return OutboundStatusLabels[raw] || raw;
};

const getEventText = (event) => {
  if (event.inboundShipment?.railCarNumber) {
    return event.inboundShipment.railCarNumber;
  } else if (event.outboundShipment?.railCarNumber) {
    return event.outboundShipment.railCarNumber;
  }
  return event.accountName;
};

// "PO 123 · Acme Lumber" — one tooltip line per sibling load.
const getShipWithMemberLine = (member) =>
  member?.shipTo?.name
    ? `PO ${member.poNumber} · ${member.shipTo.name}`
    : `PO ${member.poNumber}`;

const ScheduleEventTooltipTitle = ({ event }) => {
  const classes = useStyles();
  const isOutbound = Boolean(event.outboundId);
  const statusLabel = getScheduleShipmentStatusLabel(event);
  const mainText = getEventText(event);
  const railNumber =
    event.inboundShipment?.railCarNumber ||
    event.outboundShipment?.railCarNumber ||
    "";
  const primaryIsRail = Boolean(railNumber);
  const accountName = (event.accountName || "").trim() || null;
  const productName = getShipmentBackedProductName(event);
  const showAccountRow = primaryIsRail && accountName;

  // Linked loads (AD-12): the card's own badge has no tooltip, so the detail
  // lives here. This title only mounts while the tooltip is open, which makes
  // the sibling read lazy — nothing is fetched for a card nobody hovers — and
  // the shared cache means a re-hovered group is fetched once. A member that
  // did not load has no PO to print, so the row stays count-only.
  const shipWithIds = isLinked(event.outboundShipment)
    ? event.outboundShipment.shipWithIds
    : null;
  const shipWithKey = shipWithCacheKey(shipWithIds);
  const [shipWithMembers, setShipWithMembers] = useState([]);
  useEffect(() => {
    setShipWithMembers([]);
    if (!shipWithKey) return undefined;
    let cancelled = false;
    fetchShipWithMembersCached(shipWithKey.split(",")).then((members) => {
      if (cancelled) return;
      setShipWithMembers(
        members.filter((member) => !member?.unavailable && member?.poNumber),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [shipWithKey]);
  const shipWithSiblings = shipWithMembers.filter(
    (member) => member.id !== event.outboundId,
  );

  return (
    <Box className={classes.eventTooltipInner} component="div">
      <Typography
        component="div"
        variant="caption"
        className={classes.eventTooltipMeta}
      >
        {event.time} · {isOutbound ? "Outbound" : "Inbound"}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        className={classes.eventTooltipPrimary}
      >
        {mainText}
      </Typography>
      {showAccountRow ? (
        <Box className={classes.eventTooltipRow}>
          <Typography
            component="div"
            className={classes.eventTooltipFieldLabel}
            color="inherit"
          >
            Account
          </Typography>
          <Typography
            component="div"
            className={classes.eventTooltipFieldValue}
            color="inherit"
          >
            {accountName}
          </Typography>
        </Box>
      ) : null}
      {productName ? (
        <Box className={classes.eventTooltipRow}>
          <Typography
            component="div"
            className={classes.eventTooltipFieldLabel}
            color="inherit"
          >
            Product
          </Typography>
          <Typography
            component="div"
            className={classes.eventTooltipFieldValue}
            color="inherit"
          >
            {productName}
          </Typography>
        </Box>
      ) : null}
      {shipWithIds ? (
        <Box className={classes.eventTooltipRow}>
          <Typography
            component="div"
            className={classes.eventTooltipFieldLabel}
            color="inherit"
          >
            Ships together
          </Typography>
          <Typography
            component="div"
            className={classes.eventTooltipFieldValue}
            color="inherit"
          >
            {shipWithIds.length} loads on one truck
          </Typography>
          {shipWithSiblings.map((member) => (
            <Typography
              key={member.id}
              component="div"
              className={classes.eventTooltipFieldValue}
              color="inherit"
            >
              {getShipWithMemberLine(member)}
            </Typography>
          ))}
        </Box>
      ) : null}
      {statusLabel ? (
        <Typography
          component="div"
          className={classes.eventTooltipStatus}
          color="inherit"
        >
          {statusLabel}
        </Typography>
      ) : null}
    </Box>
  );
};

const SingleEvent = ({
  event,
  top,
  left,
  height,
  width,
  handleClick,
  disableClick = false,
  inPopover = false,
  compact = false,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isOutbound = Boolean(event.outboundId);
  const statusLabel = getScheduleShipmentStatusLabel(event);
  const railNumber =
    event.inboundShipment?.railCarNumber ||
    event.outboundShipment?.railCarNumber ||
    "";
  const accountName = (event.accountName || "").trim();
  // Only surface account on its own line when the primary text is the railcar
  // number; otherwise getEventText already shows the account (mirrors tooltip).
  const showAccount = Boolean(railNumber) && Boolean(accountName);
  const DirectionIcon = isOutbound ? OutboundIcon : InboundIcon;
  const iconColor = getScheduleDirectionColor(event);
  const accountColor = resolveShipmentBackedAccountColor(event);
  const productColor = resolveShipmentBackedProductColor(event);
  // Subtle neutral so every card carries left+right accents (same silhouette);
  // the account/product color shows when set, else this faint grey.
  const neutralAccent =
    theme.palette.type === "dark"
      ? theme.palette.grey[700]
      : theme.palette.grey[300];
  // A linked load reads as one truck (AD-12): the badge sits in the title row
  // of both card variants, and the count reaches screen readers through the
  // card label. The card has its own tooltip, so the badge's is off and the
  // sibling detail lives in ScheduleEventTooltipTitle.
  const shipsTogether = isLinked(event.outboundShipment);
  const shipsTogetherBadge = shipsTogether ? (
    <ShipsTogetherBadge
      record={event.outboundShipment}
      selfId={event.outboundId}
      variant="calendar"
      compact={compact}
      withTooltip={false}
    />
  ) : null;
  const ariaLabel = [
    isOutbound ? "Outbound shipment" : "Inbound shipment",
    getEventText(event),
    event.time,
    statusLabel,
    shipsTogether
      ? `ships together with ${event.outboundShipment.shipWithIds.length} loads`
      : null,
    disableClick ? "preview only" : null,
  ]
    .filter(Boolean)
    .join(", ");
  // Time leads the secondary line so grouped cards are tellable apart at a
  // glance (e.g. "8:00am · Cargill, Inc" vs "9:00am · NVR Homes, Inc.").
  const secondaryLine = [event.time, showAccount ? accountName : null]
    .filter(Boolean)
    .join(" · ");

  if (inPopover) {
    return (
      <Box
        component="button"
        type="button"
        className={classes.eventCardPopover}
        disabled={disableClick}
        onClick={() => handleClick(event)}
        aria-label={ariaLabel}
      >
        <span
          className={classes.eventAccountIndicator}
          style={{ backgroundColor: accountColor || neutralAccent }}
        />
        <span
          className={classes.eventProductIndicator}
          style={{ backgroundColor: productColor || neutralAccent }}
        />
        <Box className={classes.eventCardTitleRow}>
          <DirectionIcon
            className={classes.eventDirectionIcon}
            style={{ color: iconColor }}
            aria-hidden
          />
          <span className={classes.eventCardNumber}>{getEventText(event)}</span>
          {shipsTogetherBadge}
          {statusLabel && (
            <span
              className={classes.eventStatusBadge}
              style={getScheduleStatusBadgeStyle(event, theme)}
            >
              {statusLabel}
            </span>
          )}
        </Box>
        {secondaryLine && (
          <span className={classes.eventCardAccount}>{secondaryLine}</span>
        )}
      </Box>
    );
  }

  // Grid-block (non-popover) styling — only needed past the early return above.
  const baseStyle = { height };
  const statusBodyStyle = getTrackManagementTimeslotEventBodyStyle(theme);
  const tooltipAnchorStyle = getTooltipAnchorStyle({
    inPopover,
    top,
    left,
    width,
    height,
  });

  const eventButton = (
    <Box
      component="button"
      type="button"
      className={classes.eventBoxBase}
      style={{
        ...baseStyle,
        ...statusBodyStyle,
        position: "relative",
        width: "100%",
        height: "100%",
      }}
      disabled={disableClick}
      onClick={() => handleClick(event)}
      aria-label={ariaLabel}
    >
      <span
        className={classes.eventAccountIndicator}
        style={{ backgroundColor: accountColor || neutralAccent }}
      />
      <span
        className={classes.eventProductIndicator}
        style={{ backgroundColor: productColor || neutralAccent }}
      />
      <Box className={classes.eventTitleRow}>
        <DirectionIcon
          className={classes.eventDirectionIcon}
          style={{ color: iconColor }}
          aria-hidden
        />
        <Typography variant="caption" className={classes.eventText} noWrap>
          {getEventText(event)}
        </Typography>
        {shipsTogetherBadge}
        {statusLabel && (
          <span
            className={classes.eventStatusBadge}
            style={getScheduleStatusBadgeStyle(event, theme)}
          >
            {statusLabel}
          </span>
        )}
      </Box>
      {secondaryLine && !compact && (
        <Typography
          variant="caption"
          className={classes.eventAccountText}
          noWrap
        >
          {secondaryLine}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip
      interactive
      placement="top"
      arrow
      enterDelay={350}
      enterNextDelay={350}
      classes={{ tooltip: classes.eventTooltipPaper }}
      title={<ScheduleEventTooltipTitle event={event} />}
    >
      <span style={tooltipAnchorStyle}>{eventButton}</span>
    </Tooltip>
  );
};

// Popper (not Popover) so there's no modal backdrop: the pill keeps receiving
// hover events while the list is open, which is what makes open-on-hover work.
const ScheduleEventsPopover = ({
  id,
  open,
  anchorEl,
  onClose,
  events,
  handleClick,
  disableClick,
  minWidth = 200,
  maxWidth = 400,
  marginLeft,
  onMouseEnter,
  onMouseLeave,
}) => {
  const theme = useTheme();
  if (!open || !anchorEl) return null;

  return (
    <Popper
      id={id}
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: theme.zIndex.modal }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={8}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onKeyDown={(e) => e.key === "Escape" && onClose()}
          style={{
            minWidth,
            maxWidth,
            // Darker surface than the grey[800] cards so each card (and its
            // neutral accent bars) stays visible in dark mode.
            backgroundColor:
              theme.palette.type === "dark"
                ? theme.palette.grey[900]
                : undefined,
            // Cap the list height so a busy slot (10+ overlapping) scrolls
            // instead of clipping cards off the bottom.
            maxHeight: 340,
            overflowX: "hidden",
            overflowY: "auto",
            ...(marginLeft ? { marginLeft } : undefined),
          }}
        >
          <Box pl={1} pt={0.5} pb={0.5} pr={1}>
            {events.map((event) => (
              <SingleEvent
                key={getEventRenderKey(event)}
                event={event}
                height={getEventDisplayHeight(event)}
                handleClick={handleClick}
                disableClick={disableClick}
                inPopover={true}
              />
            ))}
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

const DayViewEventCluster = ({
  group,
  top,
  height,
  handleClick,
  disableClick,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const id = open ? "day-events-cluster-popover" : undefined;
  // Same cue as the week-view pill: a cluster hiding a linked truck says so.
  const hasLinked = group.events.some((e) => isLinked(e.outboundShipment));

  // Expanding is a view action, not navigation — allow it even in preview
  // (disableClick) mode. The cards inside still inherit disableClick, so they
  // won't navigate to a shipment.
  // Opens on hover too; the grace timer keeps it open while the pointer
  // travels from the pill into the list.
  const closeTimerRef = React.useRef();
  const cancelScheduledClose = () => window.clearTimeout(closeTimerRef.current);
  React.useEffect(() => cancelScheduledClose, []);

  const handleOpen = (event) => {
    cancelScheduledClose();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    cancelScheduledClose();
    setAnchorEl(null);
  };

  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(() => setAnchorEl(null), 200);
  };

  return (
    <>
      <button
        type="button"
        className={classes.dayClusterButton}
        style={{ top, height }}
        onClick={handleOpen}
        onMouseEnter={handleOpen}
        onMouseLeave={scheduleClose}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={open ? id : undefined}
        aria-label={`${group.events.length} overlapping railcars scheduled, expand to view all`}
      >
        <Box display="flex" alignItems="center" minWidth={0} flex={1}>
          <span className={classes.eventGroupCountBadge}>
            {group.events.length}
          </span>
          {hasLinked && (
            <span
              role="img"
              aria-label="Includes loads that ship together"
              className={classes.eventGroupLinkIcon}
            >
              <LinkIcon fontSize="inherit" aria-hidden />
            </span>
          )}
          <Typography variant="caption" noWrap component="span" className={classes.eventText}>
            railcars in this slot
          </Typography>
        </Box>
        <ExpandMoreIcon className={classes.eventGroupExpandIcon} aria-hidden />
      </button>

      <ScheduleEventsPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        events={group.events}
        handleClick={handleClick}
        disableClick={disableClick}
        minWidth={260}
        maxWidth={420}
        onMouseEnter={cancelScheduledClose}
        onMouseLeave={scheduleClose}
      />
    </>
  );
};

const EventGroup = ({
  group,
  top,
  height,
  handleClick,
  disableClick = false,
  smDown,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);

  // Overlapping events often share a status/direction (e.g. two Pending inbounds
  // in one slot). Carry that signal on the pill when homogeneous; stay neutral
  // when mixed so the pill never lies about a group it can't summarize.
  const events = group.events;
  const rawStatus = (e) => (e.inboundShipment || e.outboundShipment)?.status;
  const firstStatus = rawStatus(events[0]);
  const sameStatus =
    Boolean(firstStatus) && events.every((e) => rawStatus(e) === firstStatus);
  const firstOutbound = Boolean(events[0]?.outboundId);
  const sameDirection = events.every(
    (e) => Boolean(e.outboundId) === firstOutbound,
  );
  const countBadgeStyle = sameStatus
    ? { ...getScheduleStatusBadgeStyle(events[0], theme), borderColor: "transparent" }
    : undefined;
  const DirectionIcon = firstOutbound ? OutboundIcon : InboundIcon;
  // A collapsed hour can hide a linked truck; the pill carries the link glyph
  // so it still signals, and the expanded cards then carry the full badge.
  const hasLinked = events.some((e) => isLinked(e.outboundShipment));

  // Expanding is a view action, not navigation — allow it even in preview
  // (disableClick) mode. The cards inside still inherit disableClick, so they
  // won't navigate to a shipment.
  // Opens on hover too; the grace timer keeps it open while the pointer
  // travels from the pill into the list.
  const closeTimerRef = React.useRef();
  const cancelScheduledClose = () => window.clearTimeout(closeTimerRef.current);
  React.useEffect(() => cancelScheduledClose, []);

  const handleOpen = (event) => {
    cancelScheduledClose();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    cancelScheduledClose();
    setAnchorEl(null);
  };

  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(() => setAnchorEl(null), 200);
  };

  const open = Boolean(anchorEl);
  const id = open ? "events-popper" : undefined;

  // Span the full overlap window (first start → latest end) so the box
  // reads as the time block it covers; minHeight still floors short windows.
  const baseStyle = { top, height };

  // Surface the window on the pill so it's clear why these events grouped
  // (e.g. an 8:00am event running past a 9:00am one).
  const timeRangeLabel = `${format(group.groupStart, "h:mmaaa")}–${format(
    group.groupEnd,
    "h:mmaaa",
  )}`;

  return (
    <>
      <Box
        component="button"
        type="button"
        className={`${classes.eventGroupBase} ${classes.eventGroupBox}`}
        style={baseStyle}
        onClick={handleOpen}
        onMouseEnter={handleOpen}
        onMouseLeave={scheduleClose}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={open ? id : undefined}
        aria-label={
          smDown
            ? `${group.events.length} overlapping events, expand to view all`
            : `${group.events.length} overlapping schedule events, expand to view all`
        }
      >
        <Box className={classes.eventGroupSummary}>
          <Box display="flex" alignItems="center" width="100%" minWidth={0}>
            {sameDirection && (
              <DirectionIcon
                className={classes.eventDirectionIcon}
                style={{ color: getScheduleDirectionColor(events[0]) }}
                aria-hidden
              />
            )}
            <span
              className={classes.eventGroupCountBadge}
              style={countBadgeStyle}
            >
              {events.length}
            </span>
            {hasLinked && (
              <span
                role="img"
                aria-label="Includes loads that ship together"
                className={classes.eventGroupLinkIcon}
              >
                <LinkIcon fontSize="inherit" aria-hidden />
              </span>
            )}
            <Typography variant="caption" noWrap className={classes.eventText}>
              {timeRangeLabel}
            </Typography>
          </Box>
        </Box>
        <ExpandMoreIcon className={classes.eventGroupExpandIcon} aria-hidden />
      </Box>

      <ScheduleEventsPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        events={group.events}
        handleClick={handleClick}
        disableClick={disableClick}
        minWidth={200}
        maxWidth={400}
        marginLeft={20}
        onMouseEnter={cancelScheduledClose}
        onMouseLeave={scheduleClose}
      />
    </>
  );
};

// Dialog Wrapper (if needed)
export const EventsCalendarDialog = ({
  isOpen,
  setIsOpen,
  eventType,
  selectedTimeslot,
  persistedTimeslotId,
  account,
  onTimeslotPick,
  schedulingOverride = false,
}) => {
  const classes = useStyles();

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
      return; // ignore backdrop clicks
    }
    setIsOpen(false);
  };

  return (
    <Dialog
      open={isOpen}
      maxWidth="xl"
      fullWidth
      onClose={handleClose}
      disableEscapeKeyDown={false}
      PaperProps={{
        style: { maxHeight: "calc(100vh - 24px)" },
      }}
    >
      <DialogContent dividers className={classes.dialogContent}>
        <EventsCalendar
          eventType={eventType}
          selectedTimeslot={selectedTimeslot}
          persistedTimeslotId={persistedTimeslotId}
          disableClick={true}
          height="auto"
          hideNavButtons={false}
          onTimeslotPick={onTimeslotPick}
          account={account}
          schedulingOverride={schedulingOverride}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="default"
          onClick={handleClose}
          startIcon={<CancelIcon />}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
